package top.wcpe.allincore.dsl.ir.model.animation

import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyList
import top.wcpe.allincore.dsl.ir.model.internal.ReadOnlyOrderedMap
import kotlin.jvm.JvmOverloads

/**
 * UI 动画 IR。
 *
 * 编译期产出的不可变动画描述，运行时由 AnimationScheduler 驱动播放。
 * 通过 [Kind] 区分单段过渡、关键帧、串行编排、并行编排和交错编排五种结构。
 *
 * 所有集合参数会做防御式拷贝，禁止包含 null 元素。
 *
 * @throws IllegalArgumentException 任一时长为负时抛出
 */
class UiAnimation @JvmOverloads constructor(
    // getKind()/getDurationMs()/getDelayMs()/getStaggerMs()/getEasing()/getLoopMode()/getSpring()，与原 Java getter 名一致。
    // @JvmOverloads：按尾部默认参逐个丢弃生成 8/9/10 参三重构造器，复刻原 Java 三构造器签名。
    // kind/easing/props 声明可空以复刻原 Java Objects.requireNonNull 的 NullPointerException + 中文消息。
    kind: Kind?,
    /** 单段动画持续时长（毫秒）；对编排节点表示自身额外时长，通常为 0。 */
    val durationMs: Double,
    /** 启动前的延迟（毫秒），非负。 */
    val delayMs: Double,
    /** 仅 [Kind.STAGGER] 使用：子节点之间的启动间隔（毫秒），非负。 */
    val staggerMs: Double,
    easing: String?,
    props: Map<String, UiBinding>?,
    keyframes: List<UiKeyframe>?,
    children: List<UiAnimation>?,
    /** 循环模式：null=单次播放，"true"=无限循环，"mirror"=来回镜像。 */
    val loopMode: String? = null,
    /** 弹簧物理参数：null=用 easing 驱动（默认，零回归），非空=改用弹簧求解器驱动过渡。 */
    val spring: SpringSpec? = null,
) {

    /** 动画结构类型，非空。 */
    val kind: Kind = kind ?: throw NullPointerException("kind 不能为空")

    /** easing 函数名（如 linear/easeIn/easeOut/easeInOut），非空。 */
    val easing: String = easing ?: throw NullPointerException("easing 不能为空")

    /** 动画结构类型。 */
    enum class Kind {
        /** 单段过渡：在 `durationMs` 内将一组属性从当前值线性补间到目标字面量。 */
        TRANSITION,

        /** 关键帧：根据 [getKeyframes] 中的多个时间点目标值做分段插值。 */
        KEYFRAME,

        /** 串行编排：依次播放所有 [getChildren]，前一段结束后下一段开始。 */
        SEQUENCE,

        /** 并行编排：所有 [getChildren] 同时从 t=0 开始。 */
        PARALLEL,

        /** 交错编排：所有 [getChildren] 间隔 [getStaggerMs] 依次启动。 */
        STAGGER,

        /**
         * 贝塞尔路径过渡：在 `durationMs` 内沿 [getProps] 中声明的控制点
         * （quadratic 三点 p0/p1/p2 或 cubic 四点 p0/p1/p2/p3，每点 X/Y 双字段）驱动元素的
         * `translateX` 与 `translateY`。控制点字面量由 DSL 层写死或由 IR 解释器注入。
         */
        PATH_TRANSITION
    }

    /** 单段过渡的目标字面量属性集合，键为属性名，保序、不可变。 */
    val props: Map<String, UiBinding>

    /** 关键帧序列，仅 [Kind.KEYFRAME] 使用，不可变。 */
    val keyframes: List<UiKeyframe>

    /** 子动画列表，仅 SEQUENCE/PARALLEL/STAGGER 使用，不可变。 */
    val children: List<UiAnimation>

    init {
        require(durationMs >= 0.0) { "durationMs 必须非负" }
        require(delayMs >= 0.0) { "delayMs 必须非负" }
        require(staggerMs >= 0.0) { "staggerMs 必须非负" }
        this.props = copyProps(props)
        this.keyframes = copyKeyframes(keyframes)
        this.children = copyChildren(children)
    }

    override fun equals(other: Any?): Boolean {
        if (this === other) return true
        if (other !is UiAnimation) return false
        // 复刻 Double.compare + 引用相等/集合相等。
        return durationMs.compareTo(other.durationMs) == 0 &&
            delayMs.compareTo(other.delayMs) == 0 &&
            staggerMs.compareTo(other.staggerMs) == 0 &&
            kind == other.kind &&
            easing == other.easing &&
            props == other.props &&
            keyframes == other.keyframes &&
            children == other.children &&
            spring == other.spring
    }

    // 逐位复刻 Objects.hash(kind, durationMs, delayMs, staggerMs, easing, props, keyframes, children, spring)。
    override fun hashCode(): Int {
        var result = 1
        result = 31 * result + kind.hashCode()
        result = 31 * result + durationMs.hashCode()
        result = 31 * result + delayMs.hashCode()
        result = 31 * result + staggerMs.hashCode()
        result = 31 * result + easing.hashCode()
        result = 31 * result + props.hashCode()
        result = 31 * result + keyframes.hashCode()
        result = 31 * result + children.hashCode()
        result = 31 * result + (spring?.hashCode() ?: 0)
        return result
    }

    override fun toString(): String =
        "UiAnimation{" + kind +
            ",durationMs=" + durationMs +
            ",delayMs=" + delayMs +
            ",staggerMs=" + staggerMs +
            ",easing=" + easing +
            ",props=" + props.keys +
            ",keyframes=" + keyframes.size +
            ",children=" + children.size +
            ",spring=" + spring + "}"

    companion object {
        // 防御式保序拷贝，校验键值非空；null 时抛 NullPointerException（复刻原 Java Objects.requireNonNull）。
        @Suppress("SENSELESS_COMPARISON")
        private fun copyProps(props: Map<String, UiBinding>?): Map<String, UiBinding> {
            val src = props ?: throw NullPointerException("props 不能为空")
            val copied = LinkedHashMap<String, UiBinding>(src.size)
            for (entry in src.entries) {
                val key = entry.key
                val value = entry.value
                if (key == null) throw NullPointerException("prop key 不能为空")
                if (value == null) throw NullPointerException("prop value 不能为空")
                copied[key] = value
            }
            return ReadOnlyOrderedMap(copied)
        }

        @Suppress("SENSELESS_COMPARISON")
        private fun copyKeyframes(keyframes: List<UiKeyframe>?): List<UiKeyframe> {
            val src = keyframes ?: throw NullPointerException("keyframes 不能为空")
            val copied = ArrayList<UiKeyframe>(src.size)
            for (keyframe in src) {
                if (keyframe == null) throw NullPointerException("keyframe 不能为空")
                copied.add(keyframe)
            }
            return ReadOnlyList(copied)
        }

        @Suppress("SENSELESS_COMPARISON")
        private fun copyChildren(children: List<UiAnimation>?): List<UiAnimation> {
            val src = children ?: throw NullPointerException("children 不能为空")
            val copied = ArrayList<UiAnimation>(src.size)
            for (child in src) {
                if (child == null) throw NullPointerException("child 不能为空")
                copied.add(child)
            }
            return ReadOnlyList(copied)
        }
    }
}
